import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Edit2, Trash2, Eye, Link as LinkIcon, Calendar, TrendingUp, RefreshCw, MessageSquare, X } from 'lucide-react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';

export default function PaymentsTab({ contacts, allUsers, canCreate, canUpdate, onContactClick }) {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [aiInsights, setAiInsights] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });

  // Load data for each tab
  useEffect(() => {
    if (tab === 'products') loadProducts();
    else if (tab === 'links') loadPaymentLinks();
    else if (tab === 'transactions') loadTransactions();
    else if (tab === 'subscriptions') loadSubscriptions();
  }, [tab]);

  // Defensive: coerce truthy non-arrays to [] so subsequent .filter() /
  // .map() calls don't crash with "x.filter is not a function".
  const _asArray = (d) => (Array.isArray(d) ? d : []);

  const loadProducts = () => safeAsync(async () => {
    setLoading(true);
    const data = await window.zignagenixAPI?.getCRMProducts?.({});
    setProducts(_asArray(data));
    setLoading(false);
  });

  const loadPaymentLinks = () => safeAsync(async () => {
    setLoading(true);
    const data = await window.zignagenixAPI?.getCRMPaymentLinks?.({});
    setPaymentLinks(_asArray(data));
    setLoading(false);
  });

  const loadTransactions = () => safeAsync(async () => {
    setLoading(true);
    const data = await window.zignagenixAPI?.getCRMTransactions?.(filters);
    setTransactions(_asArray(data));
    setLoading(false);
  });

  const loadSubscriptions = () => safeAsync(async () => {
    setLoading(true);
    const data = await window.zignagenixAPI?.getCRMSubscriptions?.({});
    setSubscriptions(_asArray(data));
    setLoading(false);
  });

  const handleSaveProduct = () => safeAsync(async () => {
    if (!formData.name || !formData.price) { setError('Name and price required'); return; }
    if (editingId) {
      await window.zignagenixAPI?.updateCRMProduct?.(editingId, formData);
    } else {
      await window.zignagenixAPI?.createCRMProduct?.(formData);
    }
    setShowModal(false);
    setFormData({});
    setEditingId(null);
    loadProducts();
  });

  const handleDeleteProduct = (id) => safeAsync(async () => {
    if (window.confirm('Delete this product?')) {
      await window.zignagenixAPI?.deleteCRMProduct?.(id);
      loadProducts();
    }
  });

  const handleSavePaymentLink = () => safeAsync(async () => {
    if (!formData.name || !formData.product_id) { setError('Name and product required'); return; }
    await window.zignagenixAPI?.createCRMPaymentLink?.(formData);
    setShowModal(false);
    setFormData({});
    loadPaymentLinks();
  });

  const handleDeleteLink = (id) => safeAsync(async () => {
    if (window.confirm('Delete this payment link?')) {
      await window.zignagenixAPI?.deleteCRMPaymentLink?.(id);
      loadPaymentLinks();
    }
  });

  const handleRefundTransaction = (id) => safeAsync(async () => {
    const amount = prompt('Refund amount:');
    const reason = prompt('Refund reason:');
    if (amount && reason) {
      await window.zignagenixAPI?.refundCRMTransaction?.(id, { amount: parseFloat(amount), reason });
      loadTransactions();
    }
  });

  const handleCancelSubscription = (id) => safeAsync(async () => {
    if (window.confirm('Cancel this subscription?')) {
      await window.zignagenixAPI?.cancelCRMSubscription?.(id);
      loadSubscriptions();
    }
  });

  const loadAiInsights = () => safeAsync(async () => {
    setAiLoading(true);
    try {
      const response = await window.zignagenixAPI?.sendCRMAIChat?.({
        context: 'payments',
        messages: [{ role: 'user', content: 'Analyze payment trends and provide revenue insights' }]
      });
      setAiInsights(response?.message || 'No insights available');
      setShowAiPanel(true);
    } catch (e) {
      setError('Failed to load AI insights');
    }
    setAiLoading(false);
  });

  const openProductModal = (product = null) => {
    setFormData(product || { product_type: 'one_time', is_active: true });
    setEditingId(product?.id || null);
    setShowModal(true);
  };

  const openLinkModal = () => {
    setFormData({});
    setShowModal(true);
  };

  // Compute KPIs for transactions
  const totalRevenue = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const monthRevenue = transactions
    .filter(t => new Date(t.date).getMonth() === new Date().getMonth())
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const refundCount = transactions.filter(t => t.status === 'refunded').length;
  const avgTransaction = transactions.length > 0 ? totalRevenue / transactions.length : 0;

  return (
    <div style={{ padding: '20px' }}>
      {error && <div style={{ color: '#e74c3c', marginBottom: '10px' }}>{error}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
        {[
          { id: 'products', label: 'Products', icon: '📦' },
          { id: 'links', label: 'Payment Links', icon: '🔗' },
          { id: 'transactions', label: 'Transactions', icon: '💳' },
          { id: 'subscriptions', label: 'Subscriptions', icon: '📅' }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: tab === t.id ? '#3498db' : 'transparent',
              color: tab === t.id ? '#fff' : '#34495e',
              cursor: 'pointer',
              fontWeight: tab === t.id ? 'bold' : 'normal',
              borderRadius: '4px 4px 0 0'
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Products Tab */}
      {tab === 'products' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Products</h3>
            {canCreate && <button onClick={() => openProductModal()} style={btnPrimary}><Plus size={16} /> Add Product</button>}
          </div>
          {loading ? <p>Loading...</p> : products.length ? (
            <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr style={{ background: '#ecf0f1' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Price</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                      <td style={{ padding: '12px' }}>{p.name}</td>
                      <td style={{ padding: '12px' }}>${p.price?.toFixed(2)}</td>
                      <td style={{ padding: '12px' }}><Badge text={p.product_type} /></td>
                      <td style={{ padding: '12px' }}><Badge text={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? '#27ae60' : '#95a5a6'} /></td>
                      <td style={{ padding: '12px' }}>
                        {canUpdate && <button onClick={() => openProductModal(p)} style={btnSm}><Edit2 size={14} /></button>}
                        <button onClick={() => handleDeleteProduct(p.id)} style={{ ...btnSm, ...btnDanger, marginLeft: '4px' }}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={emptyMsg}>No products yet</div>}
        </div>
      )}

      {/* Payment Links Tab */}
      {tab === 'links' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Payment Links</h3>
            {canCreate && <button onClick={openLinkModal} style={btnPrimary}><Plus size={16} /> Create Link</button>}
          </div>
          {loading ? <p>Loading...</p> : paymentLinks.length ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {paymentLinks.map(link => (
                <div key={link.id} style={cardWrap}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0' }}>{link.name}</h4>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        Amount: ${link.amount?.toFixed(2)} | Views: {link.views || 0} | Conversions: {link.conversions || 0} ({((link.conversions / (link.views || 1)) * 100).toFixed(1)}%)
                      </div>
                    </div>
                    <button onClick={() => handleDeleteLink(link.id)} style={btnDanger}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : <div style={emptyMsg}>No payment links yet</div>}
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Transactions</h3>
            <button onClick={loadAiInsights} style={btnPrimary} disabled={aiLoading}><TrendingUp size={16} /> AI Insights</button>
          </div>

          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <KPI icon={DollarSign} label="Total Revenue" value={`$${totalRevenue.toFixed(2)}`} color="#27ae60" />
            <KPI icon={Calendar} label="This Month" value={`$${monthRevenue.toFixed(2)}`} color="#2E8B98" />
            <KPI icon={RefreshCw} label="Refunds" value={refundCount} color="#e74c3c" />
            <KPI icon={TrendingUp} label="Avg Transaction" value={`$${avgTransaction.toFixed(2)}`} color="#8e44ad" />
          </div>

          {/* Filters */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <select style={inp} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
            </select>
            <input type="date" style={inp} value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
            <input type="date" style={inp} value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
            <button onClick={loadTransactions} style={btnSm}><RefreshCw size={14} /> Apply</button>
          </div>

          {loading ? <p>Loading...</p> : transactions.length ? (
            <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr style={{ background: '#ecf0f1' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Contact</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Amount</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Method</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                      <td style={{ padding: '12px' }}>{new Date(t.date).toLocaleDateString()}</td>
                      <td style={{ padding: '12px' }}>
                        {t.contact_id ? (
                          <span onClick={() => onContactClick && onContactClick(t.contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                            {t.contact_name || 'N/A'}
                          </span>
                        ) : (
                          t.contact_name || 'N/A'
                        )}
                      </td>
                      <td style={{ padding: '12px', fontWeight: 'bold' }}>${t.amount?.toFixed(2)}</td>
                      <td style={{ padding: '12px' }}><Badge text={t.status} color={t.status === 'completed' ? '#27ae60' : '#f39c12'} /></td>
                      <td style={{ padding: '12px' }}>{t.method || 'Card'}</td>
                      <td style={{ padding: '12px' }}>
                        {t.status === 'completed' && <button onClick={() => handleRefundTransaction(t.id)} style={btnSm}>Refund</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={emptyMsg}>No transactions yet</div>}
        </div>
      )}

      {/* Subscriptions Tab */}
      {tab === 'subscriptions' && (
        <div>
          <h3 style={{ margin: '0 0 16px 0' }}>Subscriptions</h3>
          {loading ? <p>Loading...</p> : subscriptions.length ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {subscriptions.map(sub => (
                <div key={sub.id} style={cardWrap}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0' }}>
                        {sub.contact_id ? (
                          <span onClick={() => onContactClick && onContactClick(sub.contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                            {sub.contact_name}
                          </span>
                        ) : (
                          sub.contact_name
                        )}
                      </h4>
                      <div style={{ fontSize: '13px', color: '#7f8c8d', lineHeight: '1.6' }}>
                        <div>Product: {sub.product_name}</div>
                        <div>Status: <Badge text={sub.status} color={sub.status === 'active' ? '#27ae60' : '#e74c3c'} /></div>
                        <div>Amount: ${sub.amount?.toFixed(2)} / {sub.billing_interval}</div>
                        <div>Next Billing: {new Date(sub.next_billing_date).toLocaleDateString()}</div>
                      </div>
                    </div>
                    {sub.status === 'active' && <button onClick={() => handleCancelSubscription(sub.id)} style={btnDanger}>Cancel</button>}
                  </div>
                </div>
              ))}
            </div>
          ) : <div style={emptyMsg}>No subscriptions yet</div>}
        </div>
      )}

      {/* Product Modal */}
      {showModal && tab === 'products' && (
        <div style={overlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>{editingId ? 'Edit Product' : 'New Product'}</h2>
              <button onClick={() => setShowModal(false)} style={closeBtn}><X size={20} /></button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Name</label>
                <input style={inp} type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Description</label>
                <textarea style={{ ...inp, minHeight: '80px' }} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Price</label>
                  <input style={inp} type="number" step="0.01" value={formData.price || ''} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Type</label>
                  <select style={inp} value={formData.product_type || 'one_time'} onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}>
                    <option value="one_time">One-Time</option>
                    <option value="recurring">Recurring</option>
                    <option value="payment_plan">Payment Plan</option>
                  </select>
                </div>
              </div>
              {formData.product_type !== 'one_time' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Billing Interval</label>
                  <select style={inp} value={formData.billing_interval || 'monthly'} onChange={(e) => setFormData({ ...formData, billing_interval: e.target.value })}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Tax Rate (%)</label>
                  <input style={inp} type="number" step="0.01" value={formData.tax_rate || ''} onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Category</label>
                  <input style={inp} type="text" value={formData.category || ''} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                  <span>Active</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowModal(false)} style={{ ...btnSm, background: '#95a5a6', color: '#fff' }}>Cancel</button>
                <button onClick={handleSaveProduct} style={btnPrimary}>Save Product</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {showModal && tab === 'links' && (
        <div style={overlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>Create Payment Link</h2>
              <button onClick={() => setShowModal(false)} style={closeBtn}><X size={20} /></button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Link Name</label>
                <input style={inp} type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Product</label>
                <select style={inp} value={formData.product_id || ''} onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}>
                  <option value="">Select a product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>Custom Amount (optional)</label>
                <input style={inp} type="number" step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })} />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowModal(false)} style={{ ...btnSm, background: '#95a5a6', color: '#fff' }}>Cancel</button>
                <button onClick={handleSavePaymentLink} style={btnPrimary}>Create Link</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights Panel */}
      {showAiPanel && (
        <div style={{ ...cardWrap, marginTop: '20px', background: '#ecf9ff', borderLeft: '4px solid #3498db' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><MessageSquare size={18} /> AI Revenue Insights</h3>
            <button onClick={() => setShowAiPanel(false)} style={closeBtn}><X size={16} /></button>
          </div>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#2c3e50' }}>{aiInsights}</p>
        </div>
      )}
    </div>
  );
}
